/**
 * Typed `apiFetch` calls for the household feature: the household sheet, the
 * registration door and "my follow-ups" (`docs/API.md` §3, §6, §7, §8).
 *
 * Every payload type is imported from `./types` (itself derived from
 * `schema.d.ts`, per `docs/CONVENTIONS.md` §4) — nothing here re-describes a
 * shape by hand.
 *
 * `recordVisit`, `createRegistrationCase` and `createEngagement` never call
 * the network directly: per `docs/OFFLINE.md` §2 ("one path, tested once"),
 * every one of them always goes through the offline queue
 * (`src/offline/sync.ts`), online included. Each mints its own `client_id`,
 * enqueues, then tries an immediate `flush()`: online, that resolves within
 * the same interaction with the server's response, exactly as a direct
 * `apiFetch` call used to; offline (or if the immediate attempt just missed
 * the network), the write stays queued and the caller gets back
 * `{queued: true, clientId}` instead.
 */
import { apiFetch, ApiError } from './client';
import { enqueueWrite, flush, newClientId } from '../offline/sync';

import type { ApiErrorBody } from './types';
import type {
  Assignment,
  Engagement,
  EngagementInputRequest,
  FollowupMessage,
  Paginated,
  RegistrationCase,
  RegistrationCaseAdvanceRequest,
  RegistrationCaseInputRequest,
  Session,
  VisitInputRequest,
  VisitRecord,
} from './types';

/**
 * What `recordVisit`, `createRegistrationCase` and `createEngagement` return
 * when their write is still in the offline queue — no immediate
 * acknowledgement from the server yet (`docs/OFFLINE.md` §2).
 */
export interface QueuedWrite {
  queued: true;
  /** The idempotency key minted for this write; matches it to its eventual server row. */
  clientId: string;
}

/**
 * Enqueue one write and try to send it right away.
 *
 * @param method - `POST` (the only method the household feature queues).
 * @param path - Path relative to `/api/v1`.
 * @param body - The request payload; `client_id` is added here.
 * @returns The server's response, parsed as `T`, when the immediate `flush()`
 *   sent it; otherwise a {@link QueuedWrite} marker.
 * @throws {ApiError} When the immediate attempt got a final `4xx` (other
 *   than `429`) back — the same shape a direct `apiFetch` call used to throw,
 *   so existing callers need no new error handling for that case.
 */
async function queuedWrite<T>(
  method: 'POST',
  path: string,
  body: Record<string, unknown>,
): Promise<T | QueuedWrite> {
  const clientId = newClientId();
  await enqueueWrite({ method, path, body: { ...body, client_id: clientId }, clientId });
  const summary = await flush();

  const sent = summary.sent.find((entry) => entry.clientId === clientId);
  if (sent) return sent.response as T;

  const rejected = summary.rejected.find((entry) => entry.clientId === clientId);
  if (rejected) {
    const errorBody: ApiErrorBody = rejected.code
      ? { code: rejected.code, detail: rejected.detail }
      : { detail: rejected.detail };
    throw new ApiError(rejected.status, rejected.detail, errorBody);
  }

  return { queued: true, clientId };
}

/**
 * Fetch the door currently assigned to the caller in a session.
 *
 * @param sessionId - The canvassing session id.
 * @returns The pending assignment, or `null` when the caller holds no door
 *   (`204`, per `docs/API.md` §3).
 */
export async function getCurrentAssignment(sessionId: number): Promise<Assignment | null> {
  const assignment = await apiFetch<Assignment | undefined>(`sessions/${sessionId}/current/`);
  return assignment ?? null;
}

/**
 * Fetch a session's detail — used here only to read its `commune`, needed to
 * open an engagement (`docs/API.md` §8) at the door it holds.
 *
 * @param sessionId - The canvassing session id.
 * @returns The session.
 */
export function getSession(sessionId: number): Promise<Session> {
  return apiFetch<Session>(`sessions/${sessionId}/`);
}

/**
 * Record the outcome of a visit at the assignment's door.
 *
 * Always goes through the offline queue (see this module's docstring):
 * returns the recorded visit when the immediate `flush()` sent it, or a
 * {@link QueuedWrite} marker otherwise.
 *
 * @param assignmentId - The assignment id (from {@link getCurrentAssignment}).
 * @param input - The visit outcome, quality, note and "do not visit" flag.
 * @returns The recorded visit, or a {@link QueuedWrite} marker.
 * @throws {import('./client').ApiError} `400 invalid_visit` when the quality
 *   is missing for `exchange`/`registration_info`, out of range, or the
 *   assignment is no longer the caller's pending one — only when this was
 *   caught by the immediate `flush()` attempt; offline, the same write is
 *   queued and surfaces later in the rejects sheet instead.
 */
export function recordVisit(
  assignmentId: number,
  input: VisitInputRequest,
): Promise<VisitRecord | QueuedWrite> {
  return queuedWrite<VisitRecord>(
    'POST',
    `assignments/${assignmentId}/visit/`,
    input as unknown as Record<string, unknown>,
  );
}

/**
 * Open a registration case at a door, with the person's explicit consent.
 *
 * Always goes through the offline queue (see this module's docstring):
 * returns the opened case when the immediate `flush()` sent it, or a
 * {@link QueuedWrite} marker otherwise.
 *
 * @param doorId - The door id.
 * @param input - First name, e-mail/phone and the `consent` flag.
 * @returns The opened case with its follow-up sequence, or a {@link QueuedWrite} marker.
 * @throws {import('./client').ApiError} `400 consent_required` when
 *   `consent` is not `true` — only when this was caught by the immediate
 *   `flush()` attempt.
 */
export function createRegistrationCase(
  doorId: number,
  input: RegistrationCaseInputRequest,
): Promise<RegistrationCase | QueuedWrite> {
  return queuedWrite<RegistrationCase>(
    'POST',
    `doors/${doorId}/registration-case/`,
    input as unknown as Record<string, unknown>,
  );
}

/**
 * List the caller's own registration cases, open first ("Mes relances").
 *
 * @returns A page of the caller's registration cases.
 */
export function listMyRegistrationCases(): Promise<Paginated<RegistrationCase>> {
  return apiFetch<Paginated<RegistrationCase>>('registration-cases/');
}

/**
 * Move a registration case forward a stage.
 *
 * @param id - The case id.
 * @param input - The target stage, and a close reason when closing.
 * @returns The updated case.
 * @throws {import('./client').ApiError} `409 invalid_transition` going
 *   backwards or sideways; `400 invalid_transition` when closing without a
 *   reason.
 */
export function advanceRegistrationCase(
  id: number,
  input: RegistrationCaseAdvanceRequest,
): Promise<RegistrationCase> {
  return apiFetch<RegistrationCase>(`registration-cases/${id}/advance/`, {
    method: 'POST',
    body: JSON.stringify(input),
  });
}

/**
 * Reschedule a failed follow-up message for immediate resend.
 *
 * Commune-admin only (`docs/FOLLOWUP.md` §5); not queued offline, unlike
 * this module's other writes — rearming only ever happens from the "Mes
 * relances" screen with a live connection, and the volunteer needs the
 * server's immediate answer (in particular the `409` below) rather than a
 * silent retry later.
 *
 * @param caseId - The registration case the message belongs to.
 * @param messageId - The follow-up message id.
 * @returns The rearmed message, `scheduled` for immediate resend.
 * @throws {import('./client').ApiError} `403` when the caller does not
 *   administer the case's commune; `409 message_not_failed` when the
 *   message is not currently `failed`.
 */
export function rearmFollowupMessage(caseId: number, messageId: number): Promise<FollowupMessage> {
  return apiFetch<FollowupMessage>(`registration-cases/${caseId}/messages/${messageId}/rearm/`, {
    method: 'POST',
  });
}

/**
 * Record a non-electoral engagement taken at a door, with explicit consent.
 *
 * Always goes through the offline queue (see this module's docstring):
 * returns the created engagement when the immediate `flush()` sent it, or a
 * {@link QueuedWrite} marker otherwise.
 *
 * @param input - The engagement kind, contact details, and the `consent` flag.
 * @returns The created engagement, or a {@link QueuedWrite} marker.
 * @throws {import('./client').ApiError} `400 consent_required` when
 *   `consent` is not `true` — only when this was caught by the immediate
 *   `flush()` attempt.
 */
export function createEngagement(input: EngagementInputRequest): Promise<Engagement | QueuedWrite> {
  return queuedWrite<Engagement>(
    'POST',
    'engagements/',
    input as unknown as Record<string, unknown>,
  );
}
